import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Shield,
  Smile,
  Eye,
  Heart,
  Umbrella,
  Calendar,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  type Employee,
  type Dependent,
  type Plan,
  isHealthOrMedicalPlan,
} from "@shared/schema";
import { calculateEnrollmentPremium } from "@/lib/enrollmentCalculator";
import { formatBenefitAmount, type EnrollmentCalculationResult } from "@shared/utils/enrollmentCalculator";
// BUG-006: the plan card's price decision (priced / employer paid / can't
// price / loading) lives in one pure helper so a failed calculation can never
// render as "$0.00".
import {
  describePlanPrice,
  resolvePayCadence,
  scopeContributionsToRuleSet,
  toPerPeriod,
  type PlanPriceDisplay,
} from "@/lib/planPriceDisplay";
import { getAgeAsOfDate, resolveEnrollmentEffectiveDate } from "@shared/lib/ageDetermination";
import { eligibleDependentsForPlan } from "@shared/utils/dependentEligibility";
import {
  BeneficiaryAllocation,
  type BeneficiaryAllocationValue,
  defaultBeneficiaryAllocation,
  isBeneficiaryAllocationValid,
  mirrorPrimaryToLegacy,
  saveBeneficiaries,
} from "@/components/benefits/BeneficiaryAllocation";
import { WAIVE_REASONS, classifyDepReason, flattenDepReasons } from "@shared/utils/waiveReasons";
import { DependentsBody } from "@/components/benefits/v2/modifySteps";
import { isLifeDisabilityType, requiresBeneficiary } from "@shared/schemas/plans";
import { planTypeRank, isExclusivePlanType, normalizePlanType, isFsaPlanType, getPlanTypeMeta } from "@shared/schemas/planTypes";
import { isPlanCurrentlyAvailable } from "@/components/benefits/v2/typeIcons";
import { determineCoverageTierFromDependents } from "@/lib/enrollmentFilters";
import { computeFsaPreview, FSA_NO_PAY_SCHEDULE_ADMIN } from "@/lib/fsaPreview";
import { usePaySchedule } from "@/hooks/usePaySchedule";
import { isPayScheduleAnchored } from "@shared/utils/fsaProration";
// DEV-262: shared premium-data loader (rates + contributions incl. buy-up base plans)
import { usePlanPremiumData } from "@/hooks/usePlanPremiumData";
import { useLifeDisabilityConfigs } from "@/hooks/useLifeDisabilityConfigs";
import { ratingAreaGapMessage } from "@shared/utils/ratingAreaCoverage";

type CategorySelection = {
  type: "plan" | "waive" | null;
  planId: number | null;
  selectedDependents: number[];
  waiverReason?: string;
  waiverDescription?: string;
  beneficiaryName?: string;
  beneficiaryRelationship?: string;
  beneficiaries?: BeneficiaryAllocationValue;
  /** FSA annual election ($) — employee-only contribution amount. */
  annualElection?: string;
  /** Per-dependent waive-reason dropdown selection (slug or "other"). Mirrors
   * ModifyTypeModal/DependentsBody. Flattened into the dependentWaiverReasons
   * wire format at save time via flattenDepReasons. */
  depWaiveReasons?: Record<number, string>;
  /** Freeform "Other" text paired with `depWaiveReasons[id] === "other"`. */
  depWaiveOtherTexts?: Record<number, string>;
};

export function BenefitsConfigurationForm({
  employee,
  dependents,
  availablePlans,
  existingEnrollments = [],
  onComplete,
  isLoading,
  enrollmentSource = "employer_manual",
  restrictToTypes,
}: {
  employee: Employee;
  dependents: Dependent[];
  availablePlans: any[];
  existingEnrollments?: any[];
  onComplete: () => void;
  isLoading?: boolean;
  enrollmentSource?: string;
  /**
   * When set, only plans whose `plan.type` (normalized) matches one of these
   * ids are shown in the wizard. Used by per-section "Configure {Type}" CTAs
   * on the employee Benefits tab. Omit to show every type the client offers
   * (legacy / global "Configure Benefits" entry).
   */
  restrictToTypes?: string[];
}) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous re-entry guard. `isSubmitting` is React state and doesn't
  // flip until the next render — a same-tick double-fire can slip past the
  // disabled button. The ref blocks re-entry inside handleSubmit itself.
  const submittingRef = useRef(false);
  const [effectiveDate, setEffectiveDate] = useState<string>("");
  const [categorySelections, setCategorySelections] = useState<Record<string, CategorySelection>>({});

  // Fetch plan rates and contributions for premium calculations
  const planIds = useMemo(() => {
    return availablePlans.map((pa: any) => pa.plan?.id).filter(Boolean);
  }, [availablePlans]);

  // DEV-262: one shared loader replaces this form's three hand-rolled queries
  // (per-plan rates, per-plan contributions, and the base-plan follow-up
  // fetch). The loader returns rates + contributions for the given plans PLUS
  // any referenced buy-up base plans, keyed and flattened. Destructuring keeps
  // the previous variable names so every consumer below is unchanged.
  const {
    ratesByPlanId: planRatesData,
    ratingAreaGapsByPlanId,
    contribsByPlanId: planContributionsData,
    allPlanRates: allPlanRatesFlat,
    allPlanContributions: allPlanContributionsFlat,
    isLoading: premiumDataLoading,
    error: premiumDataError,
  } = usePlanPremiumData(planIds, employee.clientId);

  // The server prices a saved enrollment against the contribution rule set of
  // the employee's eligibility group (withComputedPremiums). The loader above
  // returns EVERY rule set's rows for a plan, and the calculator takes the
  // first match — so a plan with two groups previewed the first group's split.
  // Resolve this employee's group per plan (same endpoint the quote preview
  // uses) and scope contributions to it in calculatePlanPremium.
  type GroupResolution = { groupId: number; ruleSetId: number } | null;
  const ruleSetQueryEnabled = planIds.length > 0 && employee.clientId != null;
  const { data: ruleSetResolutions = {}, isLoading: ruleSetsLoading } = useQuery<
    Record<number, Record<number, GroupResolution>>
  >({
    queryKey: ["/api/eligibility-groups/resolve-bulk", { clientId: employee.clientId, planIds, employeeIds: [employee.id] }],
    enabled: ruleSetQueryEnabled,
    staleTime: 60_000,
    queryFn: async () => {
      const resp = await fetch("/api/eligibility-groups/resolve-bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clientId: employee.clientId, planIds, employeeIds: [employee.id] }),
      });
      // Unresolvable → price with the plan's full contribution set (today's behavior).
      if (!resp.ok) return {};
      const data = await resp.json();
      return (data?.resolutionsByPlan ?? {}) as Record<number, Record<number, GroupResolution>>;
    },
  });
  const ruleSetsPending = ruleSetQueryEnabled && ruleSetsLoading;

  // Fetch LD configs for life/disability plans
  const ldPlanIds = useMemo(() => {
    return planIds.filter((pid: number) => {
      const pa = availablePlans.find((p: any) => p.plan?.id === pid);
      return pa && isLifeDisabilityType(String(pa.plan?.type || ""));
    });
  }, [planIds, availablePlans]);

  const { ldConfigByPlanId: ldConfigData } = useLifeDisabilityConfigs(ldPlanIds);

  // Cadence every amount on this form is shown at. A missing pay cycle is
  // shown monthly AND flagged (payCadence.note) instead of a bare "per month".
  const payCadence = useMemo(() => resolvePayCadence(employee.payCycle), [employee.payCycle]);

  // Normalized allow-list of types (lowercase). When empty, no scoping is applied.
  const restrictSet = useMemo(() => {
    if (!restrictToTypes || restrictToTypes.length === 0) return null;
    const set = new Set(restrictToTypes.map((t) => t.toLowerCase().trim()));
    // Treat medical/health as equivalent so the section taxonomy works regardless of source data.
    if (set.has("medical")) set.add("health");
    if (set.has("health")) set.add("medical");
    return set;
  }, [restrictToTypes]);

  // Set of normalized plan types where the employee already has an active or
  // pending base enrollment. Used to soft-block double-enrollment for exclusive
  // types (Medical / Dental / Vision / Life / STD / LTD / FSAs). Waived rows
  // don't count — broker can still re-enroll into a waived type via the wizard.
  const activeOrPendingTypes = useMemo(() => {
    const types = new Set<string>();
    for (const enr of existingEnrollments || []) {
      if ((enr as any)?.waivedCoverage) continue;
      const status = String((enr as any)?.status || "").toLowerCase();
      if (status === "terminated") continue;
      const t = normalizePlanType((enr as any)?.plan?.type ?? (enr as any)?.planType);
      if (t) types.add(t);
    }
    return types;
  }, [existingEnrollments]);

  // Filter out plans that are already enrolled or waived, plus exclusive-typed
  // plans whose type already has an active/pending enrollment for this employee.
  const filteredPlans = useMemo(() => {
    // Duplicate client_plans links to the same plan render duplicate cards
    // that select as one (selection is keyed by plan.id) — keep the first
    // link per plan.
    const seenPlanIds = new Set<number>();
    return availablePlans.map((planAssoc: any) => {
      // The plan year this client is on is the client_plans link, not the
      // library row: a plan dated 2026-01-01 in the library can be offered to
      // this client from 2026-11-01. Everything below that prices, validates
      // or defaults an effective date must see the link's dates, or a blank
      // date landed on the library date — before the employer's plan year.
      const plan = planAssoc?.plan;
      if (!plan) return planAssoc;
      return {
        ...planAssoc,
        plan: {
          ...plan,
          effectiveDate: planAssoc.effectiveDate ?? plan.effectiveDate,
          endDate: planAssoc.endDate ?? plan.endDate,
          renewalDate: planAssoc.renewalDate ?? plan.renewalDate,
        },
      };
    }).filter((planAssoc: any) => {
      const dedupeId = planAssoc?.plan?.id;
      if (dedupeId != null) {
        if (seenPlanIds.has(dedupeId)) return false;
        seenPlanIds.add(dedupeId);
      }
      return true;
    }).filter((planAssoc: any) => {
      const plan = planAssoc.plan;
      if (!plan) return false;
      // Drop closed plans (endDate in the past) — they shouldn't be available
      // for new enrollments. Same rule the broker AvailablePlans tab uses.
      if (!isPlanCurrentlyAvailable(plan)) return false;
      if (restrictSet) {
        const type = String(plan.type || "").toLowerCase().trim();
        if (!restrictSet.has(type)) return false;
      }
      // Exclusive-type guard: if the employee already has an active/pending
      // enrollment of this type, hide all OTHER plans of the same type so the
      // wizard can't create a duplicate. Brokers use Modify > Switch plan to swap.
      const planType = normalizePlanType(plan.type);
      if (isExclusivePlanType(planType) && activeOrPendingTypes.has(planType)) {
        return false;
      }
      // Block plans the employee is currently enrolled in / waiving — but NOT
      // plans whose only row is terminated. Server's POST handler reactivates
      // terminated rows on re-enroll, so the wizard should let them through.
      return !existingEnrollments.some((enr: any) => {
        if (enr?.planId !== plan.id) return false;
        const status = String(enr?.status ?? "").toLowerCase();
        return status !== "terminated";
      });
    });
  }, [availablePlans, existingEnrollments, restrictSet, activeOrPendingTypes]);

  // Group plans by type/category, sorted by the canonical UI rank.
  const plansByType = useMemo(() => {
    const grouped = filteredPlans.reduce((acc: Record<string, Plan[]>, planAssoc: any) => {
      const plan = planAssoc.plan;
      if (!plan) return acc;
      const type = plan.type || "other";
      if (!acc[type]) acc[type] = [];
      acc[type].push(plan);
      return acc;
    }, {} as Record<string, Plan[]>);
    // Re-emit as an ordered Record so iteration follows TYPE_RANK.
    const ordered: Record<string, Plan[]> = {};
    Object.keys(grouped)
      .sort((a, b) => planTypeRank(a) - planTypeRank(b))
      .forEach((k) => { ordered[k] = grouped[k]; });
    return ordered;
  }, [filteredPlans]);

  /**
   * Coverage can't start before a plan's own plan year begins — that would rate the
   * member against the wrong year. Collect one message per offending selected plan so
   * we can show the broker exactly which plan rejects the date and what's allowed,
   * rather than silently rewriting what they typed.
   */
  const effectiveDateErrors = useMemo(() => {
    if (!effectiveDate) return [] as string[]; // blank is fine — it defaults per plan
    const errors: string[] = [];
    for (const [category, selection] of Object.entries(categorySelections)) {
      if (!selection?.type) continue;
      const plan =
        selection.type === "plan" && selection.planId
          ? filteredPlans.find((pa: any) => pa.plan?.id === selection.planId)?.plan
          : plansByType[category]?.[0];
      if (!plan) continue;
      const { tooEarly, tooLate, planStart, planEnd } = resolveEnrollmentEffectiveDate(effectiveDate, plan);
      if (tooEarly || tooLate) {
        const range = planEnd ? `${planStart} to ${planEnd}` : `${planStart} or later`;
        errors.push(`${(plan as any).name} covers ${range} — pick a date in that range.`);
      }
    }
    return errors;
  }, [effectiveDate, categorySelections, filteredPlans, plansByType]);

  // Auto-select mandatory (enrollmentRequired) plans
  useEffect(() => {
    const mandatory: Record<string, CategorySelection> = {};
    for (const pa of filteredPlans) {
      const plan = (pa as any).plan;
      if (!plan || !plan.enrollmentRequired) continue;
      const type = plan.type || "other";
      if (!mandatory[type]) {
        mandatory[type] = { type: "plan", planId: plan.id, selectedDependents: [] };
      }
    }
    if (Object.keys(mandatory).length > 0) {
      setCategorySelections((prev) => ({ ...mandatory, ...prev }));
    }
  }, [filteredPlans]);

  // Seed per-dep waive reasons from existing enrollments (set by employee or
  // broker on a prior session). The user can override before saving.
  const seededExistingReasonsRef = useRef(false);
  useEffect(() => {
    if (seededExistingReasonsRef.current) return;
    if (!existingEnrollments || existingEnrollments.length === 0) return;
    const seedByType: Record<string, { reasons: Record<number, string>; otherTexts: Record<number, string> }> = {};
    for (const enr of existingEnrollments as any[]) {
      const type = normalizePlanType(enr?.plan?.type ?? enr?.planType);
      const stored = enr?.dependentWaiverReasons;
      if (!type || !stored || typeof stored !== "object") continue;
      const bucket = seedByType[type] ?? (seedByType[type] = { reasons: {}, otherTexts: {} });
      for (const [depId, raw] of Object.entries(stored)) {
        const cls = classifyDepReason(raw as string);
        if (cls.reason) bucket.reasons[Number(depId)] = cls.reason;
        if (cls.otherText) bucket.otherTexts[Number(depId)] = cls.otherText;
      }
    }
    if (Object.keys(seedByType).length === 0) {
      seededExistingReasonsRef.current = true;
      return;
    }
    setCategorySelections((prev) => {
      const next = { ...prev };
      for (const [type, b] of Object.entries(seedByType)) {
        const base = (next[type] as CategorySelection) ?? { type: null, planId: null, selectedDependents: [] };
        next[type] = {
          ...base,
          depWaiveReasons: { ...(base.depWaiveReasons ?? {}), ...b.reasons },
          depWaiveOtherTexts: { ...(base.depWaiveOtherTexts ?? {}), ...b.otherTexts },
        };
      }
      return next;
    });
    seededExistingReasonsRef.current = true;
  }, [existingEnrollments]);

  // Calculate premium for a plan with selected dependents.
  //
  // Parity with the server's withComputedPremiums is essential: the displayed
  // deduction here must equal what lands on the saved benefit / payroll.
  // `allPlanContributions` + `allPlanRates` (including referenced base plans)
  // let the calculator resolve a buy-up plan's employer contribution;
  // `ageAsOf` + `coverageTier` match the server's rate-band and tier selection.
  const calculatePlanPremium = (
    plan: Plan,
    selectedDepIds: number[],
  ): { result: EnrollmentCalculationResult | null; error: string | null } => {
    const rates = planRatesData[plan.id] || [];
    const ruleSetId = ruleSetResolutions[plan.id]?.[employee.id]?.ruleSetId ?? null;
    const contributions = scopeContributionsToRuleSet(planContributionsData[plan.id] || [], ruleSetId);
    const selectedDeps = dependents.filter((d) => selectedDepIds.includes(d.id));
    const isLD = isLifeDisabilityType(String(plan.type || ""));
    const isFsa = isFsaPlanType(String(plan.type || ""));
    // Age-rating anchors to this date's plan year. A blank date falls back to the
    // plan's start; an out-of-range date is rejected on submit, but we still price
    // it at the plan start so the number on screen stays plan-year-correct.
    const resolved = resolveEnrollmentEffectiveDate(effectiveDate, plan);
    const enrollmentEffectiveDate =
      (resolved.tooEarly || resolved.tooLate ? resolved.planStart : resolved.effectiveDate) ||
      new Date().toISOString().split("T")[0];

    try {
      const result = calculateEnrollmentPremium({
        plan,
        planRates: rates,
        planContributions: contributions,
        employee,
        dependents: selectedDeps,
        coverageTier: isFsa
          ? "employee_only"
          : determineCoverageTierFromDependents(dependents, selectedDepIds),
        ageAsOf: getAgeAsOfDate(plan as any, employee as any, enrollmentEffectiveDate),
        allPlanContributions: allPlanContributionsFlat,
        allPlanRates: allPlanRatesFlat,
        lifeDisabilityConfig: isLD ? (ldConfigData[plan.id] ?? null) : undefined,
        employeeCompensation: isLD ? {
          compensationType: (employee as any)?.compensationType,
          compensationAmount: (employee as any)?.compensationAmount,
          salary: (employee as any)?.salary,
        } : undefined,
      });
      return { result, error: null };
    } catch (e) {
      console.error("Premium calculation error:", e);
      return { result: null, error: e instanceof Error ? e.message : String(e) };
    }
  };

  // What the card shows for this plan. FSA plans have no premium (the cost is
  // the election entered below), so they never go through here.
  const pricePlan = (
    plan: Plan,
    selectedDepIds: number[],
  ): { display: PlanPriceDisplay; result: EnrollmentCalculationResult | null } => {
    const { result, error } = calculatePlanPremium(plan, selectedDepIds);
    const display = describePlanPrice({
      result,
      payCycle: employee.payCycle,
      ratesLoading: premiumDataLoading || ruleSetsPending,
      ratesError: premiumDataError,
      calcError: error,
      rateRowCount: (planRatesData[plan.id] || []).length,
      ratingAreaGap: ratingAreaGapsByPlanId?.[plan.id]
        ? ratingAreaGapMessage(ratingAreaGapsByPlanId[plan.id], (plan as any)?.name)
        : null,
    });
    return { display, result };
  };

  // Handle plan selection for a category
  const handlePlanSelect = (category: string, planId: number | null, isWaive: boolean) => {
    // Block deselect/waive for auto-enrollment categories
    const mandatoryPlan = filteredPlans.find((pa: any) => {
      const p = (pa as any).plan;
      return p && p.enrollmentRequired && (p.type || "other") === category;
    });
    if (mandatoryPlan && (isWaive || planId !== (mandatoryPlan as any).plan?.id)) return;

    setCategorySelections((prev) => ({
      ...prev,
      [category]: {
        type: isWaive ? "waive" : planId ? "plan" : null,
        planId: isWaive ? null : planId,
        selectedDependents: prev[category]?.selectedDependents || [],
        waiverReason: isWaive ? prev[category]?.waiverReason || "" : undefined,
        waiverDescription: isWaive ? prev[category]?.waiverDescription || "" : undefined,
        beneficiaryName: prev[category]?.beneficiaryName || "",
        beneficiaryRelationship: prev[category]?.beneficiaryRelationship || "",
      },
    }));
  };

  // Handle waiver reason change
  const handleWaiverReasonChange = (category: string, reason: string) => {
    setCategorySelections((prev) => ({
      ...prev,
      [category]: { ...prev[category], waiverReason: reason },
    }));
  };

  // Get icon for plan type
  const getPlanIcon = (type: string) => {
    const lowerType = type.toLowerCase();
    if (isHealthOrMedicalPlan(lowerType)) return Shield;
    if (lowerType.includes("dental")) return Smile;
    if (lowerType.includes("vision")) return Eye;
    if (lowerType.includes("life")) return Heart;
    return Umbrella;
  };

  // Check if any selections have been made
  const hasSelections = Object.values(categorySelections).some(
    (sel) => sel.type === "plan" || sel.type === "waive"
  );

  // Calculate total cost of all selected plans
  const totalCostSummary = useMemo(() => {
    let totalMonthlyEmployeeCost = 0;
    let totalMonthlyEmployerCost = 0;
    let selectedCount = 0;
    let waivedCount = 0;
    // A plan that can't be priced must not be summed as $0: the total is
    // withheld and the plan named instead.
    const unpricedPlans: string[] = [];
    let pricingPending = false;

    for (const [, selection] of Object.entries(categorySelections)) {
      if (selection.type === "waive") {
        waivedCount++;
      } else if (selection.type === "plan" && selection.planId) {
        selectedCount++;
        const plan = filteredPlans.find((pa: any) => pa.plan?.id === selection.planId)?.plan;
        // FSA cost is the annual election entered below, not a premium.
        if (!plan || isFsaPlanType(String(plan.type || ""))) continue;
        const { display } = pricePlan(plan, selection.selectedDependents);
        if (display.kind === "priced") {
          totalMonthlyEmployeeCost += display.employeeMonthly;
          totalMonthlyEmployerCost += display.employerMonthly;
        } else if (display.kind === "employer_paid") {
          totalMonthlyEmployerCost += display.employerMonthly;
        } else if (display.kind === "loading") {
          pricingPending = true;
        } else {
          unpricedPlans.push((plan as any).name || `Plan ${plan.id}`);
        }
      }
    }

    const totalMonthly = totalMonthlyEmployeeCost + totalMonthlyEmployerCost;
    const perPayPeriodEmployee = toPerPeriod(totalMonthlyEmployeeCost, payCadence);

    return {
      totalMonthlyEmployeeCost,
      totalMonthlyEmployerCost,
      totalMonthly,
      perPayPeriodEmployee,
      selectedCount,
      waivedCount,
      unpricedPlans,
      pricingPending,
    };
  }, [
    categorySelections,
    filteredPlans,
    planRatesData,
    planContributionsData,
    allPlanRatesFlat,
    allPlanContributionsFlat,
    ldConfigData,
    ruleSetResolutions,
    ruleSetsPending,
    premiumDataLoading,
    premiumDataError,
    effectiveDate,
    dependents,
    employee,
    payCadence,
  ]);

  // Handle form submission
  const handleSubmit = async () => {
    // Synchronous re-entry guard — catches the same-tick double-invocation
    // case that the `isSubmitting` state guard misses (state hasn't flushed
    // yet on a fast double-click / event bubble / React 18 batching quirk).
    if (submittingRef.current) {
      console.warn("[BenefitsConfig] handleSubmit re-entered while in-flight; ignoring");
      return;
    }
    submittingRef.current = true;
    try {
      await runSubmit();
    } finally {
      submittingRef.current = false;
    }
  };

  const runSubmit = async () => {
    // Validate selections
    const enrollmentsToCreate: any[] = [];
    const waiversToCreate: any[] = [];

    for (const [category, selection] of Object.entries(categorySelections)) {
      if (!selection.type) continue;

      if (selection.type === "waive") {
        // Validate waiver
        if (!selection.waiverReason) {
          toast({ title: `Please select a reason for waiving ${getPlanTypeMeta(category).label} coverage`, variant: "destructive" });
          return;
        }
        // Find any plan in this category to use for the waiver record
        const categoryPlans = plansByType[category];
        if (categoryPlans && categoryPlans.length > 0) {
          waiversToCreate.push({
            category,
            planId: categoryPlans[0].id,
            reason: selection.waiverReason,
            description: selection.waiverDescription,
          });
        }
      } else if (selection.type === "plan" && selection.planId) {
        // Validate life insurance beneficiary
        const plan = filteredPlans.find((pa: any) => pa.plan?.id === selection.planId)?.plan;
        if (requiresBeneficiary(plan?.type) && !isBeneficiaryAllocationValid(selection.beneficiaries)) {
          toast({ title: "Each beneficiary needs a name + relationship, and primary (and contingent, if any) must total 100%", variant: "destructive" });
          return;
        }

        // Validate per-dep waive reasons (parity with ModifyTypeModal + employee wizard):
        // every eligible dep that's NOT enrolled needs a reason. Auto-enrollment plans
        // skip this — the existing payload IIFE fills "Auto-enrollment" for them.
        if (!(plan as any)?.enrollmentRequired) {
          const eligibleForPlan = eligibleDependentsForPlan(plan, dependents as any[]);
          const waivedEligible = eligibleForPlan.filter(
            (d: any) => !selection.selectedDependents.includes(d.id),
          );
          const reasonMap = selection.depWaiveReasons ?? {};
          const otherTextMap = selection.depWaiveOtherTexts ?? {};
          const missing = waivedEligible.filter((d: any) => {
            const r = reasonMap[d.id];
            if (!r) return true;
            if (r === "other") return !otherTextMap[d.id]?.trim();
            return false;
          });
          if (missing.length > 0) {
            const names = missing.map((d: any) => `${d.firstName} ${d.lastName}`).join(", ");
            toast({
              title: "Waiver Reason Required",
              description: `Please pick a reason for: ${names}`,
              variant: "destructive",
            });
            return;
          }
        }

        enrollmentsToCreate.push({
          planId: selection.planId,
          selectedDependents: selection.selectedDependents,
          beneficiaries: requiresBeneficiary(plan?.type) ? selection.beneficiaries : undefined,
          depWaiveReasons: selection.depWaiveReasons,
          depWaiveOtherTexts: selection.depWaiveOtherTexts,
        });
      }
    }

    if (enrollmentsToCreate.length === 0 && waiversToCreate.length === 0) {
      toast({ title: "Please select at least one plan or waiver", variant: "destructive" });
      return;
    }

    // Reject an effective date that predates any selected plan's start, and say
    // what's allowed — never silently move the broker's date.
    if (effectiveDateErrors.length > 0) {
      toast({
        title: "Effective date is outside the plan year",
        description: effectiveDateErrors.join(" "),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const enrollmentEffectiveDate = effectiveDate || new Date().toISOString().split("T")[0];

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const eff = new Date(enrollmentEffectiveDate);
      eff.setHours(0, 0, 0, 0);
      const enrollmentStatus = eff > today ? "pending" : "active";

      // Create enrollments
      for (const enrollment of enrollmentsToCreate) {
        const plan = filteredPlans.find((pa: any) => pa.plan?.id === enrollment.planId)?.plan;
        // Resolve per plan (blank → this plan's start) so age-rating anchors to the
        // right year, then derive status from that date, not the raw global one.
        // Out-of-range dates were already rejected above.
        const planEffectiveDate =
          resolveEnrollmentEffectiveDate(effectiveDate, plan).effectiveDate ||
          enrollmentEffectiveDate;
        const planEff = new Date(planEffectiveDate);
        planEff.setHours(0, 0, 0, 0);
        const planStatus = planEff > today ? "pending" : "active";
        const isFsa = isFsaPlanType(String((plan as any)?.type || ""));
        if (isFsa) {
          const v = Number(enrollment.annualElection);
          if (!enrollment.annualElection || !Number.isFinite(v) || v <= 0) {
            throw new Error(`Annual election is required for ${(plan as any)?.name ?? "FSA plan"}.`);
          }
        }
        const coverageTier = isFsa
          ? "employee_only"
          : determineCoverageTierFromDependents(dependents, enrollment.selectedDependents);
        // Mirror primary[0] into the legacy columns; full set persisted via /replace below.
        const legacy = mirrorPrimaryToLegacy(enrollment.beneficiaries);

        // Create main enrollment with explicit dependent selection
        const response = await fetch("/api/enrollments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: employee.clientId,
            employeeId: employee.id,
            planId: enrollment.planId,
            coverageTier,
            status: planStatus,
            enrollmentSource,
            effectiveDate: planEffectiveDate,
            beneficiaryName: isFsa ? null : legacy.beneficiaryName,
            beneficiaryRelationship: isFsa ? null : legacy.beneficiaryRelationship,
            waivedCoverage: false,
            // FSA: no dependents per IRS rule. Other plans: pass explicit IDs.
            selectedDependentIds: isFsa ? [] : enrollment.selectedDependents,
            // Decimal column → send as a string (server Zod rejects a number).
            ...(isFsa ? { annualElection: String(Number(enrollment.annualElection)) } : {}),
            // Dependent waiver reasons. For mandatory (enrollmentRequired) plans
            // we still auto-fill "Auto-enrollment" for unenrolled deps; for
            // ordinary plans we send the broker's per-dep dropdown selections
            // flattened to the legacy Record<depId, string> wire format.
            ...((() => {
              if (isFsa) return {};
              if ((plan as any)?.enrollmentRequired && dependents.length > 0) {
                const unenrolledDeps = dependents.filter((d: any) => !enrollment.selectedDependents.includes(d.id));
                if (unenrolledDeps.length > 0) {
                  const reasons: Record<string, string> = {};
                  unenrolledDeps.forEach((d: any) => { reasons[d.id] = "Auto-enrollment"; });
                  return { dependentWaiverReasons: reasons };
                }
                return {};
              }
              const flat = flattenDepReasons(enrollment.depWaiveReasons, enrollment.depWaiveOtherTexts);
              return flat ? { dependentWaiverReasons: flat } : {};
            })()),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create enrollment");
        }

        // Persist the full beneficiary set against the just-created base enrollment.
        if (!isFsa && enrollment.beneficiaries) {
          const created = await response.json().catch(() => ({}));
          if (created?.id) {
            await saveBeneficiaries(created.id, enrollment.beneficiaries, {
              employeeId: employee.id,
              planId: enrollment.planId,
            });
          }
        }

        // No per-dependent POST loop here — the server's
        // POST /api/enrollments handler reads `selectedDependentIds` from the
        // body above and bulk-inserts one enrollment row per dependent itself
        // (enrollmentRoutes.ts ~658-672, calls storage.bulkCreateEnrollments).
        // Doing it again on the client created a duplicate-active-enrollment
        // collision against the rows the server just created.
      }

      // Create waivers
      for (const waiver of waiversToCreate) {
        const waiverPlan = filteredPlans.find((pa: any) => pa.plan?.id === waiver.planId)?.plan;
        // Same per-plan resolution as enrollments so a blank date lands on the
        // plan's start instead of today (which the server would reject).
        const waiverEffectiveDate =
          resolveEnrollmentEffectiveDate(effectiveDate, waiverPlan).effectiveDate ||
          enrollmentEffectiveDate;
        const waiverEff = new Date(waiverEffectiveDate);
        waiverEff.setHours(0, 0, 0, 0);
        const waiverStatus = waiverEff > today ? "pending" : "active";

        const waiverRes = await fetch("/api/enrollments", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: employee.clientId,
            employeeId: employee.id,
            planId: waiver.planId,
            coverageTier: "employee_only",
            status: waiverStatus,
            enrollmentSource,
            effectiveDate: waiverEffectiveDate,
            waivedCoverage: true,
            eventType: waiver.reason,
            eventDescription: waiver.description || null,
          }),
        });
        if (!waiverRes.ok) {
          const err = await waiverRes.json().catch(() => ({}));
          throw new Error(err.message || err.error || "Failed to waive enrollment");
        }
      }

      toast({ title: "Benefits configured successfully", variant: "success" });
      onComplete();
    } catch (error: any) {
      console.error("Error configuring benefits:", error);
      toast({ title: error.message || "Failed to configure benefits", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading available plans...</p>
        </div>
      </div>
    );
  }

  if (Object.keys(plansByType).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p className="mb-2">
          {availablePlans.length === 0
            ? "No plans available for this client."
            : "All available plans are already enrolled or waived."}
        </p>
        <p className="text-sm">
          {availablePlans.length === 0
            ? "Please configure client plans first in the client's Benefits tab."
            : "You can modify existing enrollments using the Modify button on each benefit card."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4">
      {/* Benefit Categories */}
      {Object.entries(plansByType).map(([category, plans]: [string, Plan[]]) => {
        const Icon = getPlanIcon(category);
        const selection = categorySelections[category] || { type: null, planId: null, selectedDependents: [] };
        const selectedPlan = plans.find((p) => p.id === selection.planId);
        const eligibleDeps = eligibleDependentsForPlan(selectedPlan, dependents);
        const selectedPricing =
          selectedPlan && !isFsaPlanType(String(selectedPlan.type || ""))
            ? pricePlan(selectedPlan, selection.selectedDependents)
            : null;

        const isMandatoryLocked = plans.some(p => Boolean((p as any).enrollmentRequired));

        return (
          <div key={category} className="space-y-4">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5" />
              <h3 className="text-lg font-semibold">{getPlanTypeMeta(category).label}</h3>
            </div>

            {/* Plan Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Individual Plan Cards */}
              {plans.map((plan) => {
                const isSelected = selection.type === "plan" && selection.planId === plan.id;
                const isMandatory = Boolean((plan as any).enrollmentRequired);
                const isFsaPlan = isFsaPlanType(String(plan.type || ""));
                const pricing = isFsaPlan ? null : pricePlan(plan, isSelected ? selection.selectedDependents : []);
                const planPremium = pricing?.result ?? null;

                return (
                  <Card
                    key={plan.id}
                    className={`transition-all ${
                      isMandatoryLocked && isSelected
                        ? "border-2 border-primary bg-primary/5 ring-2 ring-primary/20 cursor-not-allowed"
                        : isMandatoryLocked && !isSelected
                          ? "border border-gray-200 opacity-50 cursor-not-allowed"
                          : isSelected
                            ? "border-2 border-primary bg-primary/5 ring-2 ring-primary/20 cursor-pointer"
                            : "border hover:border-gray-400 hover:shadow-sm cursor-pointer"
                    }`}
                    onClick={() => handlePlanSelect(category, plan.id, false)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm break-words">{plan.name}</h5>
                          <p className="text-xs text-muted-foreground break-words">{(plan as any).carrier || ""}</p>
                        </div>
                        {isMandatory && isSelected ? (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30 flex-shrink-0 ml-2">Auto-Enrollment</Badge>
                        ) : isSelected ? (
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                        ) : null}
                      </div>
                      {/* Life/disability: benefit amount when known; the calculator's
                          warnings ("Coverage not configured…") now surface through
                          the price line below as the reason the plan can't be priced. */}
                      {isLifeDisabilityType(String(plan.type || "")) && planPremium?.benefitAmount != null && (
                        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
                          Benefit: {formatBenefitAmount(planPremium.benefitAmount, planPremium.benefitAmountPeriod)}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t" data-testid={`plan-price-${plan.id}`}>
                        {pricing ? (
                          <PlanPriceLine display={pricing.display} />
                        ) : (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground" data-testid="plan-price-election">
                              Cost set by your annual election
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Waive Card */}
              <Card
                className={`transition-all ${
                  isMandatoryLocked
                    ? "border border-dashed opacity-50 cursor-not-allowed"
                    : selection.type === "waive"
                      ? "border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20 ring-2 ring-amber-500/20 cursor-pointer"
                      : "border border-dashed hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/10 cursor-pointer"
                }`}
                onClick={() => handlePlanSelect(category, null, true)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="font-medium text-sm">Waive Coverage</h5>
                      <p className="text-xs text-muted-foreground">Decline this benefit</p>
                    </div>
                    {selection.type === "waive" && (
                      <CheckCircle className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-right">
                      <span className="text-lg font-bold text-muted-foreground">$0.00</span>
                      <p className="text-xs text-muted-foreground">no cost</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Waiver Reason (when waive is selected) */}
            {selection.type === "waive" && (
              <div className="ml-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                <div>
                  <Label className="text-sm font-medium">Reason for Waiving *</Label>
                  <Select
                    value={selection.waiverReason || ""}
                    onValueChange={(val) => handleWaiverReasonChange(category, val)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {WAIVE_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* FSA annual election (employee-only — no dependent picker per IRS rule) */}
            {selection.type === "plan" && selectedPlan && isFsaPlanType(selectedPlan.type) && (
              <FsaElectionInput
                planId={selectedPlan.id}
                value={selection.annualElection || ""}
                onChange={(v) =>
                  setCategorySelections((prev) => ({
                    ...prev,
                    [category]: { ...(prev[category] as CategorySelection), annualElection: v },
                  }))
                }
                payCycle={employee.payCycle}
                clientId={employee.clientId}
                payGroupId={(employee as any).payGroupId}
              />
            )}

            {/* Dependent Selection — only dependents this plan covers (none for FSA/Life/AD&D/Disability).
                Uses the same DependentsBody widget as ModifyTypeModal + the employee wizard:
                Enroll/Waive button pair + per-dep waive-reason dropdown (with Other textarea). */}
            {selection.type === "plan" && selectedPlan && eligibleDeps.length > 0 && !(selectedPlan as any).enrollmentRequired && (
              <div className="ml-4 space-y-3">
                <DependentsBody
                  dependents={eligibleDeps as any[]}
                  depDecisions={Object.fromEntries(
                    eligibleDeps.map((d: any) => [
                      d.id,
                      selection.selectedDependents.includes(d.id) ? "enroll" : "waive",
                    ]),
                  ) as Record<number, "enroll" | "waive">}
                  onSetDecision={(id, decision) =>
                    setCategorySelections((prev) => {
                      const base = (prev[category] as CategorySelection) ?? { type: "plan", planId: null, selectedDependents: [] };
                      const nextIds = decision === "enroll"
                        ? Array.from(new Set([...base.selectedDependents, id]))
                        : base.selectedDependents.filter((x) => x !== id);
                      // Enrolling clears any prior waive reason for that dep.
                      const nextReasons = { ...(base.depWaiveReasons ?? {}) };
                      const nextOther = { ...(base.depWaiveOtherTexts ?? {}) };
                      if (decision === "enroll") {
                        delete nextReasons[id];
                        delete nextOther[id];
                      }
                      return {
                        ...prev,
                        [category]: { ...base, selectedDependents: nextIds, depWaiveReasons: nextReasons, depWaiveOtherTexts: nextOther },
                      };
                    })
                  }
                  depWaiveReasons={selection.depWaiveReasons ?? {}}
                  onSetDepWaiveReason={(id, v) =>
                    setCategorySelections((prev) => {
                      const base = (prev[category] as CategorySelection) ?? { type: "plan", planId: null, selectedDependents: [] };
                      return {
                        ...prev,
                        [category]: { ...base, depWaiveReasons: { ...(base.depWaiveReasons ?? {}), [id]: v } },
                      };
                    })
                  }
                  depWaiveOtherTexts={selection.depWaiveOtherTexts ?? {}}
                  onSetDepWaiveOtherText={(id, v) =>
                    setCategorySelections((prev) => {
                      const base = (prev[category] as CategorySelection) ?? { type: "plan", planId: null, selectedDependents: [] };
                      return {
                        ...prev,
                        [category]: { ...base, depWaiveOtherTexts: { ...(base.depWaiveOtherTexts ?? {}), [id]: v } },
                      };
                    })
                  }
                />

                {/* Updated Cost Display */}
                {selectedPricing && (
                  <div className="pt-3 border-t border-stone-200 dark:border-stone-700">
                    <div className="flex items-center justify-between text-sm gap-3">
                      <span className="text-muted-foreground">Updated Cost:</span>
                      <div data-testid="plan-price-updated">
                        <PlanPriceLine display={selectedPricing.display} compact />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Beneficiary Section (for life + AD&D) */}
            {selection.type === "plan" && requiresBeneficiary(selectedPlan?.type) && (
              <div className="ml-4">
                <BeneficiaryAllocation
                  value={selection.beneficiaries ?? defaultBeneficiaryAllocation()}
                  onChange={(beneficiaries) =>
                    setCategorySelections((prev) => ({
                      ...prev,
                      [category]: { ...(prev[category] as CategorySelection), beneficiaries },
                    }))
                  }
                />
              </div>
            )}

            <Separator />
          </div>
        );
      })}

      {/* Effective Date Section */}
      <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <Label className="text-sm font-medium">Effective Date (Optional)</Label>
        </div>
        <Input
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          className="max-w-xs"
          aria-invalid={effectiveDateErrors.length > 0}
        />
        {effectiveDateErrors.length > 0 ? (
          <div className="space-y-1">
            {effectiveDateErrors.map((message) => (
              <p key={message} className="text-xs text-destructive">
                {message}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Leave blank to start coverage on each plan's plan-year start date for this
            employer. Coverage can't begin before the plan year starts.
          </p>
        )}
      </div>

      {/* Total Cost Summary - Always visible */}
      <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border border-purple-200 dark:border-purple-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-purple-900 dark:text-purple-100">
              Total Elections Summary
            </h4>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              {totalCostSummary.selectedCount === 0 && totalCostSummary.waivedCount === 0
                ? "Select plans above to see cost"
                : `${totalCostSummary.selectedCount} plan${totalCostSummary.selectedCount !== 1 ? "s" : ""} selected${totalCostSummary.waivedCount > 0 ? `, ${totalCostSummary.waivedCount} waived` : ""}`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-testid="elections-total">
              {totalCostSummary.pricingPending
                ? "…"
                : totalCostSummary.unpricedPlans.length > 0
                  ? "Unavailable"
                  : formatCurrency(totalCostSummary.perPayPeriodEmployee)}
            </p>
            <p className="text-sm text-purple-600 dark:text-purple-400" data-testid="elections-total-cadence">
              Employee cost {payCadence.label}
            </p>
          </div>
        </div>
        {totalCostSummary.unpricedPlans.length > 0 && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400" data-testid="elections-total-unavailable">
            Can't total: {totalCostSummary.unpricedPlans.join(", ")} couldn't be priced — see the plan card for the reason.
          </p>
        )}
        {payCadence.note && (
          <p className="mt-2 text-xs text-purple-700 dark:text-purple-300" data-testid="pay-cycle-assumed">
            {payCadence.note}
          </p>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !hasSelections}
          className="gap-2"
        >
          {isSubmitting ? "Saving..." : "Save Benefits"}
          <CheckCircle className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(amount);

/**
 * The price line on a plan card / the Updated Cost row. One branch per
 * PlanPriceDisplay kind — there is no path that prints "$0.00" for a plan
 * that couldn't be priced (BUG-006).
 */
function PlanPriceLine({ display, compact = false }: { display: PlanPriceDisplay; compact?: boolean }) {
  const amountClass = compact ? "font-bold text-primary" : "text-lg font-bold text-primary";
  switch (display.kind) {
    case "loading":
      return (
        <div className="text-right">
          <span className="text-sm text-muted-foreground" data-testid="plan-price-loading">
            {display.message}
          </span>
        </div>
      );
    case "unavailable":
      return (
        <div
          className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400"
          data-testid="plan-price-unavailable"
          title={display.reasons.join("\n")}
        >
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span className="break-words">{display.message}</span>
        </div>
      );
    case "employer_paid":
      return (
        <div className="text-right">
          <span className={amountClass} data-testid="plan-price-employer-paid">
            {display.message}
          </span>
          <p className="text-xs text-muted-foreground">
            employer pays {formatCurrency(display.employerAmount)} {display.cadence.label}
          </p>
        </div>
      );
    case "priced":
      return (
        <div className="text-right">
          <span className={amountClass} data-testid="plan-price-amount">
            {formatCurrency(display.amount)}
          </span>
          {compact ? (
            <span className="text-xs text-muted-foreground ml-1" data-testid="plan-price-cadence">
              {display.cadence.short}
            </span>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="plan-price-cadence">
              {display.cadence.label}
              {display.cadence.assumed ? " (pay cycle not set)" : ""}
            </p>
          )}
        </div>
      );
  }
}

function FsaElectionInput({
  planId,
  value,
  onChange,
  payCycle,
  clientId,
  payGroupId,
}: {
  planId: number;
  value: string;
  onChange: (v: string) => void;
  payCycle?: string | null;
  clientId?: number | null;
  payGroupId?: number | null;
}) {
  const { data: fsaConfig } = useQuery<any>({
    queryKey: ["/api/plans", planId, "fsa-config"],
    queryFn: async () => {
      const r = await fetch(`/api/plans/${planId}/fsa-config`);
      if (!r.ok) return null;
      return r.json();
    },
    staleTime: Infinity,
  });
  // FSA needs a payroll schedule that yields an EXACT deduction (no estimate).
  const { resolvePaySchedule, isLoading: payScheduleLoading } = usePaySchedule(clientId);
  const payScheduleReady = isPayScheduleAnchored(resolvePaySchedule(payGroupId));
  const preview = computeFsaPreview(value, fsaConfig, payCycle);
  const min = fsaConfig?.annualElectionMin != null ? Number(fsaConfig.annualElectionMin) : null;
  const max = preview.annualCap;
  // No usable max → election can't be bounded; block entry until the plan is configured.
  const notConfigured = !(max != null && max > 0);
  const formatUSD = (amount: number) =>
    amount.toLocaleString(undefined, { style: "currency", currency: "USD" });

  if (notConfigured) {
    return (
      <div className="ml-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-800 rounded-lg flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          This FSA plan has no annual election limit configured. Set the maximum
          election in the plan&apos;s FSA configuration before enrolling — otherwise
          the election can&apos;t be capped.
        </span>
      </div>
    );
  }

  if (!payScheduleReady && !payScheduleLoading) {
    return (
      <div className="ml-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-800 rounded-lg flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{FSA_NO_PAY_SCHEDULE_ADMIN}</span>
      </div>
    );
  }

  return (
    <div className="ml-4 p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-lg space-y-3">
      <div>
        <Label className="text-sm font-medium">
          Annual election ($) <span className="text-amber-700">*</span>
        </Label>
        <Input
          type="number"
          inputMode="decimal"
          min={min ?? undefined}
          max={max ?? undefined}
          step="1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={min != null && max != null ? `Between $${min} and $${max}` : "Enter annual amount"}
          className="mt-1"
        />
        {(min != null || max != null) && (
          <div className="text-[10.5px] text-stone-500 mt-1">
            {min != null && max != null
              ? `Plan range: $${min.toLocaleString()} – $${max.toLocaleString()}`
              : min != null
                ? `Plan minimum: $${min.toLocaleString()}`
                : `Plan maximum: $${max!.toLocaleString()}`}
            {fsaConfig?.carryoverAllowed && fsaConfig?.carryoverMax && (
              <> · Carryover allowed up to ${Number(fsaConfig.carryoverMax).toLocaleString()}</>
            )}
            {preview.employerContribution > 0 && (
              <> · Employer contributes ${preview.employerContribution.toLocaleString()}/year</>
            )}
          </div>
        )}
      </div>
      {preview.employeeAnnualIsValid && (
        <div className="rounded-md border border-stone-200 bg-white dark:bg-stone-900 dark:border-stone-700 px-3 py-2 text-xs">
          <div className="font-mono font-semibold text-stone-900 dark:text-stone-100">
            {formatUSD(preview.perPaycheckEmployeeContribution)}{" "}
            <span className="text-stone-500 font-normal">{preview.perPaycheckLabel}</span>
          </div>
          {preview.employerContribution > 0 && (
            <div className="text-stone-500 mt-0.5">
              Combined annual: ${preview.combinedAnnual.toLocaleString()} (employee + employer)
            </div>
          )}
        </div>
      )}
      {preview.overCap && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
          Combined employer (${preview.employerContribution.toLocaleString()}) + employee (${preview.employeeAnnual.toLocaleString()}) exceeds the plan max (${preview.annualCap!.toLocaleString()}).
        </div>
      )}
    </div>
  );
}
